import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft } from "lucide-react";
import StaffHeader from "@/components/StaffHeader";
import PhotoUpload from "@/components/PhotoUpload";
import AnimalTypeSelector from "@/components/AnimalTypeSelector";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function NewCase() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [photos, setPhotos] = useState([]);
  const [animalName, setAnimalName] = useState("");
  const [animalType, setAnimalType] = useState("Dog");
  const [speciesOther, setSpeciesOther] = useState("");
  const [caseHistory, setCaseHistory] = useState("");
  const [breed, setBreed] = useState("");
  const [color, setColor] = useState("");
  const [admissionDate, setAdmissionDate] = useState(new Date().toISOString().slice(0, 10));
  const [externalPhotoLink, setExternalPhotoLink] = useState("");
  const [donationNeeded, setDonationNeeded] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!animalName.trim()) {
      toast({ variant: "destructive", title: "Animal name is required" });
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/cases", {
        animal_name: animalName.trim(),
        animal_type: animalType,
        species_other: animalType === "Other" ? speciesOther : null,
        breed,
        color,
        admission_date: admissionDate,
        case_history: caseHistory,
        external_photo_link: externalPhotoLink || null,
        donation_needed: donationNeeded ? parseFloat(donationNeeded) : 0,
        photos,
      });
      toast({ title: "Case saved!", description: `${data.animal_name}'s case has been created.` });
      navigate(`/cases/${data.id}?created=1`);
    } catch (err) {
      toast({ variant: "destructive", title: "Could not save case", description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-amtm-bg">
      <StaffHeader />
      <div className="max-w-lg mx-auto px-4 py-6 pb-24">
        <button onClick={() => navigate(-1)} data-testid="new-case-back-button" className="flex items-center gap-1 text-sm text-amtm-textMuted mb-4">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <h1 className="font-display text-3xl text-amtm-text mb-6">New Case</h1>

        <form onSubmit={handleSave} className="space-y-6" data-testid="new-case-form">
          <div>
            <Label className="text-base mb-2 block">Photo</Label>
            <PhotoUpload photos={photos} setPhotos={setPhotos} testId="new-case-photo-upload" />
          </div>

          <div>
            <Label htmlFor="animal-name" className="text-base">Animal Name</Label>
            <Input
              id="animal-name"
              value={animalName}
              onChange={(e) => setAnimalName(e.target.value)}
              placeholder="e.g. Motu"
              required
              data-testid="new-case-name-input"
              className="mt-1.5 h-14 text-lg"
            />
          </div>

          <div>
            <Label className="text-base mb-2 block">Animal Type</Label>
            <AnimalTypeSelector
              value={animalType}
              onChange={setAnimalType}
              speciesOther={speciesOther}
              onSpeciesOtherChange={setSpeciesOther}
              testIdPrefix="new-case-animal-type"
            />
          </div>

          <div>
            <Label htmlFor="case-history" className="text-base">Condition / Case History</Label>
            <Textarea
              id="case-history"
              value={caseHistory}
              onChange={(e) => setCaseHistory(e.target.value)}
              placeholder="e.g. Maggot wound on leg"
              data-testid="new-case-history-input"
              className="mt-1.5 min-h-[80px] text-base"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="breed" className="text-base">Breed</Label>
              <Input id="breed" value={breed} onChange={(e) => setBreed(e.target.value)} placeholder="Mongrel" data-testid="new-case-breed-input" className="mt-1.5 h-12" />
            </div>
            <div>
              <Label htmlFor="color" className="text-base">Color</Label>
              <Input id="color" value={color} onChange={(e) => setColor(e.target.value)} placeholder="Brown" data-testid="new-case-color-input" className="mt-1.5 h-12" />
            </div>
          </div>

          <div>
            <Label htmlFor="admission-date" className="text-base">Date of Admission</Label>
            <Input
              id="admission-date"
              type="date"
              value={admissionDate}
              onChange={(e) => setAdmissionDate(e.target.value)}
              data-testid="new-case-admission-date-input"
              className="mt-1.5 h-12"
            />
          </div>

          <div>
            <Label htmlFor="donation-needed" className="text-base">Donation Needed (₹)</Label>
            <Input
              id="donation-needed"
              type="number"
              min="0"
              value={donationNeeded}
              onChange={(e) => setDonationNeeded(e.target.value)}
              placeholder="0"
              data-testid="new-case-donation-needed-input"
              className="mt-1.5 h-12"
            />
          </div>

          <div>
            <Label htmlFor="external-link" className="text-base">External Photo Link (optional)</Label>
            <Input
              id="external-link"
              value={externalPhotoLink}
              onChange={(e) => setExternalPhotoLink(e.target.value)}
              placeholder="Google Drive link"
              data-testid="new-case-external-link-input"
              className="mt-1.5 h-12"
            />
          </div>

          <Button type="submit" disabled={saving} className="w-full h-14 text-lg font-semibold bg-amtm-teal text-white hover:bg-amtm-tealHover rounded-xl shadow-md" data-testid="save-case-button">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save Case"}
          </Button>
        </form>
      </div>
    </div>
  );
}
